import type { ValueControlProps } from "./formControl.types";

type SingleIdPickerValueProps<Id> = Pick<
    ValueControlProps<Id | null, HTMLElement>,
    "value" | "onValueChange"
> & { multiple?: false };

type MultipleIdPickerValueProps<Id> = Pick<
    ValueControlProps<Id[], HTMLElement>,
    "value" | "onValueChange"
> & { multiple: true };

/**
 * Value props for id pickers: one id or `null` by default, an id array with
 * `multiple`.
 */
export type IdPickerValueProps<Id> =
    | SingleIdPickerValueProps<Id>
    | MultipleIdPickerValueProps<Id>;

/** An id picker's value as an id array: `[]` for `null`, `[id]` for one id. */
export const toIdArray = <Id extends string | number>(
    value: Id | null | Id[],
): Id[] => {
    if (Array.isArray(value)) {
        return value;
    }
    return value === null ? [] : [value];
};

/**
 * Single-mode pick from a multi-select list change: the id just added, else
 * the one left, else `null` once the picked id is toggled off.
 */
export const pickSingleId = <Id>({
    previousIds,
    nextIds,
}: {
    previousIds: readonly Id[];
    nextIds: readonly Id[];
}): Id | null =>
    nextIds.find((id) => !previousIds.includes(id)) ?? nextIds[0] ?? null;
